import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { interval, Subject } from 'rxjs';

import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPFormFieldType } from 'src/app/core/models/enums/DPFormFieldType';
import { AccountService } from 'src/app/core/services/account.service';
import {
  CodeModalInputData,
  CodeModalType,
} from 'src/app/app-modals/types/modal-params';
import { DPSimpleAPIResponse } from 'src/app/core/models/DPSimpleAPIResponse';
import { FormParserService } from 'src/app/core/services/form-parser.service';

/**
 * Code modal to show the flow of updating email or phone number.
 */
@Component({
  selector: 'app-code-modal',
  templateUrl: './code-modal.component.html',
  styleUrls: ['./code-modal.component.scss'],
})
export class CodeModalComponent implements OnInit, OnDestroy {
  // Code modal type. It can be "email" or "phone"
  @Input() type: CodeModalType = 'email';

  // Current step value. This value determines which step the code modal will display from.
  @Input() set step(value: number) {
    this.currentStep = value;
  }

  // Code modal input data.
  @Input() inputData: CodeModalInputData;

  // Whether to show success message step at the end of the flow
  @Input() showSuccessMessage: boolean;

  // Dismiss event emitter
  @Output() dismiss = new EventEmitter();

  // Success event emitter
  @Output() success = new EventEmitter();

  // Form fields in Enter code step
  fields: DPFormField[] = [
    {
      type: DPFormFieldType.SingleLine,
      label: '',
      name: 'code',
      inputPlaceholder: 'Enter Code',
      inputType: 'number',
      colSpan: {
        desktop: 12,
        tablet: 12,
        mobile: 12,
      },
    },
  ];
  // Angular Form Group object in Enter code step
  codeForm: UntypedFormGroup;

  // Phone number input field
  inputFields: DPFormField[];

  // Phone number input form
  inputForm: UntypedFormGroup;

  // Current step value
  currentStep = 0;

  // Error text
  error: string = '';

  // Success text
  successText: string = '';

  loading = false;

  // Long code from server
  private longCode = '';

  // Resend code cooldown
  resendCooldown = 0;
  private destroy$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private formParser: FormParserService
  ) {
    this.codeForm = this.formParser.createDynamicForm(
      this.fields,
      {
        code: '',
      },
      true
    );
    this.createInputForm();
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Dismiss event handler
   */
  dismissModal() {
    this.dismiss.emit();
  }

  /**
   * Finish flow after successful update
   */
  finishFlow() {
    this.success.emit();
  }

  /**
   * Show input form step.
   */
  showInputForm() {
    this.currentStep = 1;
  }

  /**
   * Send request to server to send code to the user.
   */
  sendCodeToPhone() {
    this.inputData = this.inputForm.value;
    this.sendCode();
  }

  /**
   * Send code.
   *
   * 2 things happen on the server
   *   - It sends short code to user's email or phone.
   *   - It generates and returns long code.
   *
   * The long code and short code will be submitted together to update Email or Phone.
   */
  sendCode() {
    this.loading = true;
    this.error = '';

    this.accountService.generateCode(this.inputData).subscribe(
      (response: string | DPSimpleAPIResponse) => {
        if (typeof response === 'string') {
          this.currentStep = 3;
          this.longCode = response;
          this.startResendCooldown();
        } else {
          this.error = response.message;
        }
      },
      () => {
        this.error = 'There was an error in generating code. Please try again.';
      },
      () => {
        this.loading = false;
      }
    );
  }

  /**
   * Start resend code cooldown timer.
   * Prevents users from spamming the resend button.
   */
  private startResendCooldown() {
    this.resendCooldown = 30;
    interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--;
        }
      });
  }

  /**
   * Send codes to the server for verification.
   */
  verifyCode() {
    const { code } = this.codeForm.value;
    this.loading = true;
    
    this.accountService
      .verifyCode({
        LongCode: this.longCode,
        ShortCode: code,
      })
      .pipe(
        catchError(() => {
          throw 'Cannot verify the code';
        }),
        filter((response) => {
          if (!response.success) {
            throw response.message;
          }
          return response.success;
        }),
        // if code verification is successful, send request to server to update Email or Phone number
        switchMap(() =>
          this.type === 'phone'
            ? this.accountService.updatePhoneNumber(this.inputData.PhoneNumber)
            : this.accountService.updateUserName(this.inputData.UserName)
        ),
        catchError(() => {
          throw this.type === 'phone'
            ? 'Updating phone number failed'
            : 'Updating email failed';
        })
      )
      .subscribe(
        (response) => {
          if (response.success) {
            /**
             * If updating phone number or email is successful, show success message in the final step or finish flow.
             */
            if (this.showSuccessMessage) {
              this.currentStep = 4;
              this.successText =
                this.type === 'email'
                  ? 'Email was updated successfully'
                  : 'Phone number was updated successfully';
            } else {
              this.finishFlow();
            }
          } else {
            /**
             * If verification or update failed, show error message.
             */
            this.error = response.message;
          }
        },
        (err: string) => {
          /**
           * If verification or update failed, show error message.
           */
          this.error = err;
        },
        () => {
          this.loading = false;
        }
      );
  }

  /**
   * Creat phone number input form
   */
  private createInputForm() {
    this.inputFields = [
      {
        type: DPFormFieldType.SingleLine,
        label: 'Phone Number',
        name: 'PhoneNumber',
        colSpan: {
          desktop: 12,
          tablet: 12,
          mobile: 12,
        },
        inputMask: '(000) 000-0000',
        showMask: true,
        autoFocus: true,
      },
    ];

    this.inputForm = this.formParser.createDynamicForm(
      this.inputFields,
      {},
      true
    );
    this.inputForm
      .get('PhoneNumber')
      ?.setValidators(Validators.pattern(/^\d{10}$/));
  }
}
